import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import {Observable} from 'rxjs/index';

import { Configuration as config } from '../configuration/projects.config';
import {SendGridList} from '../models/send-grid-list';
import {SendGridSender} from '../models/send-grid-sender';
import {SendGridCampaign} from '../models/send-grid-campaign';
import {ExportedOffersLog} from '../models/exported-offers-log';
import {Question} from '../models/question';
import {InvitedPartnerIndexer} from '../models/invited-partner-indexer';
import {InvitedPartner} from '../models/project';

@Injectable()
export class ProjectService {

  constructor(private http: HttpClient) {}

  getProjects(type: string, query: Object, operation?: string) {
    const subQS = new Set();
    let qs = Object.keys(query)
      .map(key => {
        if (!Array.isArray(query[key])) {
          return key + '=' + query[key];
        }

        if (Array.isArray(query[key])) {
          return key + '[]=' + query[key].map(value => {
            const splittedValue = value.split(' - ');

            if (splittedValue[1] && key === 'state') {
              subQS.add(splittedValue[1]);
            }

            return splittedValue[0];
          }).join(`&${key}[]=`);
        }
      }).join('&');

    subQS.forEach(subQsValue => {
      qs += `&${subQsValue}=true`;
    });

    const url = `${config.apiUrl}/${config.urls.get(type)}${operation ? '/operation' : ''}?${qs}`;

    return this.http.get(`${url}`);
  }

  getProjectById<T>(type: string, projectId: string) {
    return this.http.get<{data: T}>(`${config.apiUrl}/${config.urls.getById(type)}/${projectId}`)
      .pipe(map(res => res.data));
  }

  updateProject<T>(type: string, id: string, update: T, onlyInvited?: boolean) {
    let url = `${config.apiUrl}/${config.urls.update(type)}/${id}`;
    if (onlyInvited) {
      url += '/invitedPartners';
    }

    return this.http.put(url, update);
  }

  removeAccessedPartner(projectId: string, partnerId: string) {
    return this.http.delete(`${config.apiUrl}/${config.urls.removeAccessedPartner(projectId, partnerId)}`)
  }

  getProjectCommentsForPartner(projectId: string, customerId: string, partnerId: string) {
    return this.http
      .get(`${config.apiUrl}/${config.urls.comments}/${projectId}/customer/${customerId}/partner/${partnerId}`)
      .pipe(map((res: any) => res.data));
  }

  getProjectSupplierOffer<T>(projectId: string, partnerId: string): Observable<T> {
    return this.http
      .get(`${config.apiUrl}/${config.urls.partnerOffer(projectId, partnerId)}`)
      .pipe(map((res: {data: T}) => res.data));
  }

  getProjectBuyerOffer<T>(projectId: string): Observable<T> {
    return this.http
      .get(`${config.apiUrl}/${config.urls.getAllOffers}/${projectId}`)
      .pipe(map((res: {data: T}) => res.data));
  }

  uploadFile(file: any) {
    const formData: FormData = new FormData();
    const blob = new Blob([file], { type: file.type });
    formData.append('file', blob, file.name);

    return this.http.post(`${config.apiUrl}/${config.urls.upload}`, formData);
  }

  approveProject(projectId: string) {
    return this.http
      .put(`${config.apiUrl}/${config.urls.approve}/${projectId}/verify`, {});
  }

  deleteProject(type: string, projectId: string, isPermanent: boolean) {
    const isPermanentDeleteUrl = isPermanent ? config.urls.permanentDelete(type) : config.urls.delete(type);
    return this.http.delete(`${config.apiUrl}/${isPermanentDeleteUrl}/${projectId}`);
  }

  verifyReview(projectId: string, partnerId: string) {
    return this.http.put(`${config.apiUrl}/${config.urls.verifyReview}/${projectId}/${partnerId}`, {});
  }

  changeRFQState(projectId: string, state: string, body = {}) {
    return this.http
      .put(`${config.apiUrl}/${config.urls.changeState}/${state}/${projectId}`, body);
  }

  getSendGridLists(): Observable<{result: SendGridList[]}> {
    return this.http
      .get<{result: SendGridList[]}>(`${config.apiUrl}/${config.urls.sendGridLists}`);
  }

  getSendGridSenders(): Observable<SendGridSender[]> {
    return this.http
      .get<SendGridSender[]>(`${config.apiUrl}/${config.urls.sendGridSenders}`);
  }

  submitSendGridCampaign(campaign: SendGridCampaign) {
    return this.http
      .post(`${config.apiUrl}/${config.urls.sendGridCampaign}`, campaign);
  }

  rejectAllAlternatives(projectId: string, offerId: string, itemDetails: {itemId: string, versionId: string}) {
    return this.http.put(`${config.apiUrl}/${config.urls.rejectAlternatives(projectId, offerId)}`, itemDetails);
  }

  chooseAlternatives(projectId: string, offerId: string,
                     itemDetails: {itemId: string, alterId: string, versionId: string}) {
    return this.http.put(`${config.apiUrl}/${config.urls.approveAlternative(projectId, offerId)}`, itemDetails);
  }

  getExportedOffersLog(projectId: string): Observable<{data: ExportedOffersLog[]}> {
    return this.http.get<{data: ExportedOffersLog[]}>(`${config.apiUrl}/${config.urls.exportedOffersLog}/${projectId}`);
  }

  getAllQuestions(projectId: string, isActive: boolean): Observable<{data: Question[]}> {
    return this.http
      .get<{data: Question[]}>(`${config.apiUrl}/${config.urls.question.get}/${projectId}?is_active=${isActive}`);
  }

  updateQuestion(questionId: string, answer: Partial<Question>) {
    return this.http.put(`${config.apiUrl}/${config.urls.question.update}/${questionId}`, answer);
  }

  deleteQuestion(questionId: string, isPermanent: boolean) {
    const url = isPermanent ? config.urls.question.remove : config.urls.question.delete;
    return this.http.delete(`${config.apiUrl}/${url}/${questionId}`);
  }

  getInvitedPartners(query: any): Observable<{data: InvitedPartnerIndexer[]}> {
    const qs = Object.keys(query).map(key => {
      if (Array.isArray(query[key])) {
        return query[key].map(value => `${key}[]=${value}`).join('&');
      }
      return query[key];
    }).join('&');

    const url = `${config.apiUrl}/${config.urls.getInvitedPartners}?${qs}`;
    return this.http.get<{data: InvitedPartnerIndexer[]}>(url);
  }

  getAdminNames() {
    return this.http.get(`${config.apiUrl}/${config.urls.getAdminName}`);
  }

  getUsers(search: string): Observable<any> {
    const url = `${config.apiUrl}/${config.urls.searchUsers}?search=${search}`;
    return this.http.get<any>(url).pipe(map((res: any) => res.data));
  }

  addPotentialPartners(projectId: string, partners: any[]) {
    return this.http
      .put( `${config.apiUrl}/${config.urls.addPotentialPartners}/${projectId}`, {partners});
  }

  rejectAlternative(projectId: string, offerId: string, alterType: string, itemDetails: {
    itemId: string,
    alterId?: string,
    versionId: string,
    rejection?: {reason: string, notes?: string},
  }) {
    const url = `${config.apiUrl}/${config.urls.rejectAlter(alterType, projectId, offerId)}`;

    return this.http.put(url, itemDetails);
  }

  approveAlternatives(projectId: string, offerId: string, alterType: string,
                      itemDetails: {itemId: string, alterId?: string, versionId: string}) {
    const url = `${config.apiUrl}/${config.urls.approveAlter(alterType, projectId, offerId)}`;

    return this.http.put(url, itemDetails);
  }

  unRejectAlternative(projectId: string, offerId: string, alterType: string,
                      itemDetails: {itemId: string, alterId?: string, versionId: string}) {
    const url = `${config.apiUrl}/${config.urls.unRejectAlter(alterType, projectId, offerId)}`;

    return this.http.put(url, itemDetails);
  }

  unApproveAlternatives(projectId: string, offerId: string, alterType: string,
                        itemDetails: {itemId: string, alterId?: string, versionId: string}) {
    const url = `${config.apiUrl}/${config.urls.unApproveAlter(alterType, projectId, offerId)}`;

    return this.http.put(url, itemDetails);
  }

  unlockSupplier(projectId: string, partnerId: string) {
    const url = `${config.apiUrl}/${config.urls.unlockSupplier(projectId)}`;

    return this.http.put(`${url}/${partnerId}`, {});
  }

  resendProjectInvitation(projectId: string, partner: Partial<InvitedPartner>) {
    const url = `${config.apiUrl}/${config.urls.resentInvitation(projectId)}`;

    return this.http.put(url, {partner});
  }

  getSubCategoryCompanies(query) {
    const qs = Object.keys(query).map(key => {
      if (Array.isArray(query[key])) {
        return query[key].map(id => `${key}[]=${id}`).join('&');
      }

      return `${key}=${query[key]};`
    }).join('&');

    const url = `${config.apiUrl}/${config.urls.getPartnersBySubCategory}?${qs}`;
    return this.http.get(url)
  }

  getPartnerProjects(partnerId: string, query: any) {
    const qs = Object.keys(query).map(key => {
      if (Array.isArray(query[key])) {
        return query[key].map(id => `${key}[]=${id}`).join('&');
      }

      return `${key}=${query[key]}`;
    }).join('&');

    const url = `${config.apiUrl}/${config.urls.getPartnerProjects(partnerId)}?${qs}`;

    return this.http.get(url)
  }

  getActiveCustomerNames() {
    const url = `${config.apiUrl}/${config.urls.getCustomers()}`;

    return this.http.get(url)
  }

  getProjectPartnerStatistics(projectId: string, partnerIds: string[]) {
    const qs = partnerIds.map(id => `partner[]=${id}`).join('&');
    const url = `${config.apiUrl}/${config.urls.getProjectPartnerStatistics(projectId)}?${qs}`;


    return this.http.get(url)
  }
}
